/**
 * Backups and the database at a glance, under the report's figures (item J,
 * docs/saidiseire-varundus.md): one state pill and a few short lines, the
 * newest copies first because "is there a backup from last night" is the
 * question this block answers.
 *
 * The pill is judged against the site's package (`expect`): without a package
 * nothing is "late", the facts are only shown. The server's findings above
 * the change list carry the warnings; this block carries the facts.
 */
import { DatabaseBackup } from "lucide-react";
import { cn } from "@wa/ui";
import {
  autoloadHeavy,
  backupState,
  backupStateTone,
  cleanupState,
  destinations,
  expectKey,
  type BackupState,
} from "../../../../lib/monitoringBackup";
import type { ReporterExpectView, ReporterReportView } from "../../../../lib/monitoringReporter";
import { StatusPill, fmtBytes, fmtDateTime } from "../../../ui";
import type { T } from "../words";
import { agoText, cleanupText, scheduleText, storageText } from "./backupWords";

interface Line {
  text: string;
  tone?: "warn" | "bad";
}

function backupLines(t: T, report: ReporterReportView, state: BackupState, now: number): Line[] {
  const b = report.backup;
  const out: Line[] = [];
  if (state === "outdated") out.push({ text: t("monitoring.reporter.backup.outdatedBody", { version: report.pluginVersion }), tone: "warn" });
  if (!b) return out;
  if (b.otherTools.length > 0) out.push({ text: t("monitoring.reporter.backup.otherTools", { names: b.otherTools.join(", ") }) });
  if (b.updraftInactive) out.push({ text: t("monitoring.reporter.backup.updraftInactive"), tone: "warn" });
  if (b.tool !== "updraftplus") return out;

  out.push(
    b.lastDbAt
      ? {
          text: t("monitoring.reporter.backup.lastDb", { ago: agoText(t, b.lastDbAt, now), date: fmtDateTime(b.lastDbAt) }),
          tone: state === "late" ? "bad" : undefined,
        }
      : { text: t("monitoring.reporter.backup.lastDbNone"), tone: "warn" },
  );
  out.push(
    b.lastFilesAt
      ? { text: t("monitoring.reporter.backup.lastFiles", { ago: agoText(t, b.lastFilesAt, now), date: fmtDateTime(b.lastFilesAt) }) }
      : { text: t("monitoring.reporter.backup.lastFilesNone"), tone: "warn" },
  );
  if (b.lastRun && !b.lastRun.ok) {
    out.push({ text: t("monitoring.reporter.backup.lastRunFailed", { date: fmtDateTime(b.lastRun.at) }), tone: "warn" });
  }
  const where = destinations(b);
  out.push({
    text: t("monitoring.reporter.backup.where", { list: where.map((id) => storageText(t, id)).join(", ") }),
    tone: where[0] === "none" ? "warn" : undefined,
  });
  // UpdraftPlus treats an unset schedule as manual.
  out.push({
    text: t("monitoring.reporter.backup.schedule", { db: scheduleText(t, b.dbEvery ?? "manual"), files: scheduleText(t, b.filesEvery ?? "manual") }),
  });
  return out;
}

function databaseLines(t: T, report: ReporterReportView, expect: ReporterExpectView): Line[] {
  const d = report.database;
  if (!d) return [];
  const out: Line[] = [];
  if (d.sizeBytes !== null) out.push({ text: t("monitoring.reporter.backup.dbSize", { size: fmtBytes(d.sizeBytes) }) });
  if (d.autoloadBytes !== null) {
    out.push({
      text: t("monitoring.reporter.backup.autoload", { size: fmtBytes(d.autoloadBytes) }),
      tone: autoloadHeavy(d.autoloadBytes) ? "warn" : undefined,
    });
  }
  const cleanup = cleanupText(t, d);
  if (cleanup) out.push({ text: cleanup, tone: expect.dbCleanup && cleanupState(d) !== "on" ? "warn" : undefined });
  return out;
}

export function BackupBlock({ report, expect, t }: { report: ReporterReportView; expect: ReporterExpectView; t: T }) {
  const state = backupState(report, expect);
  const lines = [...backupLines(t, report, state, Date.now()), ...databaseLines(t, report, expect)];
  const asks = expectKey(expect);
  return (
    <div className="px-[26px] pt-5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <DatabaseBackup className="crm-mut size-4" strokeWidth={1.8} />
        <p className="crm-prow-n">{t("monitoring.reporter.backup.title")}</p>
        {state !== "unknown" ? (
          <StatusPill tone={backupStateTone(state, expect)}>{t(`monitoring.reporter.backup.state.${state}`)}</StatusPill>
        ) : null}
      </div>
      {lines.length > 0 ? (
        <ul className="grid gap-x-6 gap-y-1 text-[13px] lg:grid-cols-2">
          {lines.map((line) => (
            <li
              key={line.text}
              className={cn(
                "min-w-0 break-words",
                line.tone === "bad" && "text-[var(--color-destructive)]",
                line.tone === "warn" && "text-[var(--color-warning)]",
              )}
            >
              {line.text}
            </li>
          ))}
        </ul>
      ) : null}
      {asks ? <p className="crm-mut mt-2 text-[12.5px]">{t(asks)}</p> : null}
    </div>
  );
}
